import Big from 'big.js';
import * as models from 'smg/admin/models';
import axios from 'smg/axios';
import * as colorscale from 'smg/colorscale';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import * as util from 'smg/util';
import i18n from '../i18n';

function themeColor(cssVar: string, fallback: string): string {
    const value = getComputedStyle(document.body).getPropertyValue(cssVar).trim();
    return value || fallback;
}

// Built at first use so CSS vars are available (and language-prefixed bootstrap paths don't leave us with empty stops).
let growthScale: colorscale.ColorScale | undefined;
function growthColorScale(): colorscale.ColorScale {
    if (!growthScale) {
        const colors: Record<string, number> = {
            [themeColor('--bs-info', '#0dcaf0')]: 1000,     // 1000%+ growth (extremely high)
            [themeColor('--bs-primary', '#0d6efd')]: 100,   // 100%+ growth (very high)
            [themeColor('--bs-success', '#198754')]: 10,    // 10%+ growth (positive)
            [themeColor('--bs-warning', '#ffc107')]: 0,     // 0% growth (break-even)
            [themeColor('--bs-danger', '#dc3545')]: -0.01,  // Any negative growth
        };
        growthScale = new colorscale.ColorScale(colors);
    }
    return growthScale;
}

declare interface row {
    position: number;
    company: string;
    companyName: string;
    currTotal: Big;
    prevTotal: Big;
    ratio: Big;
}

export const Options = {
    currentIndex: 0,
    currentDesc: false,
    product: 'Lapel Pins',
};

export async function ProductLeaderboard(parentElement: HTMLElement, index: number, desc: boolean = false, product: string = '') {
    return axios.get('/stats/leaderboard').then(d => {
        const data = models.StatsLeaderboardResponse.fromJSON(d.data);
        const leaderboards = data.leaderboards;

        const card = template.Clone('#product-leaderboards').firstElementChild as HTMLElement;
        const tables: HTMLElement[] = [];
        for (const l of leaderboards) {
            tables.push(drawLeaderboardTables(parentElement, l, index, desc, product, false));
        }

        const container = card.querySelector('.product-leaderboards-row') as HTMLElement;
        for (const t of tables) {
            container.appendChild(t);
        }

        const oldCard = parentElement.querySelector('.product-leaderboard');
        if (oldCard) {
            parentElement.replaceChild(card, oldCard);
        } else {
            parentElement.appendChild(card);
        }
    }).catch(() => {
        toast.Show(i18n('couldNotLoadProductLeaderboards'), ['bg-danger']);
    });
}

function drawLeaderboardTables(parentElement: HTMLElement, l: models.LeaderboardProduct, index: number, desc: boolean, product: string, redrawElement: boolean = false): HTMLElement {
    const table = template.Clone('#product-leaderboards-table').firstElementChild as HTMLTableElement;
    (table.querySelector('.product-name') as HTMLElement).innerText = l.product;
    (table.querySelector('.current-year') as HTMLElement).innerText = new Date().getFullYear().toString();

    (table.querySelector('table') as HTMLTableElement).classList.add(l.product.toLowerCase().replace(' ', '-'));

    const rows: row[] = [];
    let currTotalTotal = Big(0);
    let prevTotalTotal = Big(0);
    for (const c of l.companies) {
        let ratio = Big(0);
        if (!c.prevTotal.eq(0)) {
            ratio = c.currTotal.sub(c.prevTotal).div(c.prevTotal).mul(100);
        }

        rows.push({
            position: 0,
            company: `<i class="${c.icon} fa-fw"></i> ${c.company}`,
            companyName: c.company,
            currTotal: c.currTotal,
            prevTotal: c.prevTotal,
            ratio,
        });

        currTotalTotal = currTotalTotal.add(c.currTotal);
        prevTotalTotal = prevTotalTotal.add(c.prevTotal);
    }

    const ratioSort = rows.sort((a, b) => a.ratio.gt(b.ratio) ? -1 : 1);
    let sorted: row[] = [];
    let position = 1;
    ratioSort.forEach(s => {
        s.position = position++;
    });

    const shouldShowAsc = desc && l.product === product;

    switch (index) {
    case 0: {
        const positionEle = table.querySelector('.position') as HTMLElement;
        positionEle.classList.toggle('asc', shouldShowAsc);
        sorted = ratioSort.sort((a, b) => a.position > b.position ? 1 : -1);
        break;
    }
    case 1: {
        const companyEle = table.querySelector('.company') as HTMLElement;
        companyEle.classList.toggle('asc', shouldShowAsc);
        sorted = ratioSort.sort((a, b) => a.companyName.localeCompare(b.companyName));
        break;
    }
    case 2: {
        const totalEle = table.querySelector('.current-year') as HTMLElement;
        totalEle.classList.toggle('asc', shouldShowAsc);
        sorted = ratioSort.sort((a, b) => a.currTotal.gt(b.currTotal) ? 1 : -1);
        break;
    }
    case 3: {
        const deltaEle = table.querySelector('.delta') as HTMLElement;
        deltaEle.classList.toggle('asc', shouldShowAsc);
        sorted = ratioSort.sort((a, b) => a.ratio.gt(b.ratio) ? 1 : -1);
        break;
    }
    }

    if (desc && l.product === product) {
        sorted = sorted.reverse();
    }

    let h = '';
    for (const s of sorted) {
        const color = !s.prevTotal.eq(0) ? growthColorScale().hexWithIsDark(s.ratio.toNumber()) : null;
        let backgroundColor = '';
        let textColor = '';
        if (color) {
            backgroundColor = `background-color:${color.hex};`;
            textColor = `color:${color.isDark ? 'white' : 'black'};`;
        }
        h += `
            <tr>
                <td style="${backgroundColor}${textColor}">${s.position}</td>
                <td style="${backgroundColor}${textColor}">${s.company}</td>
                <td style="${backgroundColor}${textColor}" class="text-center">${util.DollarFormat(s.currTotal, false, true)}</td>
                <td style="${backgroundColor}${textColor}" class="text-center">${s.prevTotal.eq(0) ? '' : percentFormat(s.ratio)}</td>
            </tr>
        `;
    }

    let percentTotal: Big;
    if (!prevTotalTotal.eq(0)) {
        percentTotal = currTotalTotal.sub(prevTotalTotal).div(prevTotalTotal);
    } else {
        percentTotal = Big(0);
    }
    const s = growthColorScale().hexWithIsDark(percentTotal.toNumber());
    (table.querySelector('.month-total') as HTMLElement).innerText = util.DollarFormat(currTotalTotal, false, true);
    if (!prevTotalTotal.eq(0)) {
        (table.querySelector('.percent-change') as HTMLElement).innerText = percentFormat(currTotalTotal.sub(prevTotalTotal).div(prevTotalTotal).mul(100));
    } else {
        (table.querySelector('.percent-change') as HTMLElement).innerText = '∞';
    }
    (table.querySelector('tbody') as HTMLElement).innerHTML = h;

    const footers = table.querySelectorAll('tfoot th, tfoot td');
    footers.forEach(f => {
        if (s) {
            const ele = f as HTMLElement;
            ele.style.backgroundColor = s.hex;
            ele.style.color = s.isDark ? 'white' : 'black';
        }
    });

    const headers = table.querySelectorAll('thead th');
    headers.forEach((ele, i) => {
        ele.addEventListener('click', e => {
            e.preventDefault();
            Options.currentIndex = i;
            Options.currentDesc = !ele.classList.contains('asc');
            Options.product = l.product;
            drawLeaderboardTables(parentElement, l, Options.currentIndex, Options.currentDesc, Options.product, true);
        });
    });

    if (redrawElement) {
        const productNames = parentElement.querySelectorAll('table');
        productNames.forEach(e => {
            if (e.classList.contains(l.product.toLowerCase().replace(' ', '-'))) {
                e.replaceWith(table.querySelector('table') as HTMLTableElement);
            }
        });
    }

    return table;
}

const percentFormat = (s: Big): string => `${s.toFixed(2)}%`;